import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate, Navigate, useLocation } from "react-router-dom";
import { Menu, X, Bell, LogOut, LayoutDashboard, Search, PlusCircle, Volume2, VolumeX, BellRing } from "lucide-react";
import LiveAlerts, { soundOn, chime } from "./LiveAlerts";
import { useAuth } from "../context/AuthContext";
import { api, dashFor, ago } from "../lib/api";
import { Loader } from "./kit";
import { AlertsBanner } from "./PushAlerts";

const NAV = [["/search", "Find a Professional"], ["/search-jobs", "Search Jobs"], ["/post-job", "Post a Job"], ["/adverts", "Service Adverts"], ["/services", "Services"], ["/how-it-works", "How It Works"],
  ["/homeowners", "For Homeowners"], ["/for-professionals", "For Professionals"], ["/pricing", "Pricing"], ["/advice", "Advice & Inspiration"], ["/about", "About Us"]];

export const BrandLogo = ({ className = "h-12" }) => <img src="/brand/my_hh_logo.png" alt="MyHomelyHub" className={`${className} w-auto max-w-none shrink-0`} />;

function Notifications() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [sound, setSound] = useState(soundOn());
  const [perm, setPerm] = useState("Notification" in window ? Notification.permission : "unsupported");
  const { user, refresh } = useAuth();
  const toggleSound = () => {
    const v = !sound;
    localStorage.setItem("mhh_sound", v ? "on" : "off");
    setSound(v);
    if (v) chime();
  };
  const toggle = async () => {
    setOpen(!open);
    if (!open) {
      const { data } = await api.get("/notifications");
      setItems(data);
      await api.post("/notifications/read-all");
      refresh();
    }
  };
  return (
    <div className="relative">
      <LiveAlerts />
      <button onClick={toggle} className="relative p-2 rounded-full hover:bg-slate-100" aria-label="Notifications" data-testid="notifications-btn">
        <Bell size={20} className="text-slate-600" />
        {user.unread_notifications > 0 && <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-[#FF8166] text-white text-[10px] grid place-items-center">{user.unread_notifications}</span>}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 card max-h-96 overflow-auto z-50 p-2" data-testid="notifications-panel">
          <div className="flex items-center justify-between gap-2 px-2 pb-2 border-b border-slate-100 mb-1">
            <button onClick={toggleSound} className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-[#FF8166]" data-testid="alert-sound-toggle">{sound ? <Volume2 size={14} /> : <VolumeX size={14} />}Sound {sound ? "on" : "off"}</button>
            {perm === "default" && <button onClick={async () => setPerm(await Notification.requestPermission())} className="inline-flex items-center gap-1 text-xs font-semibold text-[#FF8166]" data-testid="enable-desktop-alerts-btn"><BellRing size={14} />Enable desktop alerts</button>}
            {perm === "granted" && <span className="text-xs text-emerald-600">Desktop alerts on</span>}
          </div>
          {items.length === 0 && <p className="p-4 text-sm muted">You're all caught up.</p>}
          {items.map((n) => (
            <Link key={n.id} to={n.link || "#"} onClick={() => setOpen(false)} className="block p-3 rounded-xl hover:bg-slate-50">
              <div className="text-sm font-semibold text-slate-800">{n.title}</div>
              <div className="text-xs text-slate-500 line-clamp-2">{n.body}</div>
              <div className="text-[11px] text-slate-400 mt-1">{ago(n.at)}</div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function Header() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();
  useEffect(() => setOpen(false), [loc.pathname]);
  return (
    <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-slate-100">
      <div className="max-w-[1880px] mx-auto px-4 sm:px-6 lg:px-8 h-20 sm:h-24 flex items-center justify-between gap-6">
        <Link to="/" className="shrink-0" data-testid="header-logo"><BrandLogo className="h-11 sm:h-[72px]" /></Link>
        <nav className="hidden min-[1800px]:flex flex-1 justify-center items-center gap-3.5 min-w-0" aria-label="Main">
          {NAV.map(([to, l]) => <NavLink key={to} to={to} className={({ isActive }) => `whitespace-nowrap text-[13px] font-medium transition-colors ${isActive ? "text-[#FF8166]" : "text-[#3C4E60] hover:text-[#FF8166]"}`} data-testid={`nav-${to.slice(1)}`}>{l}</NavLink>)}
        </nav>
        <div className="flex items-center gap-2 shrink-0">
          {user ? (
            <>
              <Notifications />
              <Link to={dashFor(user)} className="btn-ghost btn-sm hidden sm:inline-flex" data-testid="header-dashboard-btn"><LayoutDashboard size={14} />Dashboard</Link>
              <button onClick={async () => { await logout(); nav("/"); }} className="p-2 rounded-full hover:bg-slate-100" aria-label="Log out" data-testid="header-logout-btn"><LogOut size={18} className="text-slate-600" /></button>
            </>
          ) : (
            <>
              <Link to="/login" className="hidden sm:inline whitespace-nowrap text-sm font-semibold text-[#374B5C] px-3" data-testid="header-login-btn">Log In</Link>
              <Link to="/register?role=provider" className="btn-ghost btn-sm whitespace-nowrap hidden md:inline-flex" data-testid="header-join-pro-btn">Join as a Professional</Link>
            </>
          )}
          <Link to="/post-job" className="btn-coral btn-sm whitespace-nowrap hidden sm:inline-flex" data-testid="header-post-job-btn">Post a Job</Link>
          <button className="min-[1800px]:hidden p-2" onClick={() => setOpen(!open)} aria-label="Menu" aria-expanded={open} data-testid="mobile-menu-btn">{open ? <X /> : <Menu />}</button>
        </div>
      </div>
      {open && (
        <div className="min-[1800px]:hidden border-t border-slate-100 bg-white fade-up" data-testid="mobile-menu">
          <div className="container-x py-4 grid gap-1">
            {NAV.map(([to, l]) => <Link key={to} to={to} className="py-3 px-2 rounded-lg hover:bg-slate-50 font-medium text-slate-700">{l}</Link>)}
            {!user && <div className="grid grid-cols-2 gap-2 mt-2"><Link to="/login" className="btn-ghost">Log In</Link><Link to="/register?role=provider" className="btn-navy">Join as a Pro</Link></div>}
            {user && <Link to={dashFor(user)} className="btn-navy mt-2">My Dashboard</Link>}
          </div>
        </div>
      )}
    </header>
  );
}

export function Footer() {
  const { settings } = useAuth();
  const b = settings?.branding || {};
  const cols = [
    ["Homeowners", [["/search", "Find a Professional"], ["/post-job", "Post a Job"], ["/how-it-works", "How It Works"], ["/advice", "Advice"], ["/cost-guides", "Cost Guides"], ["/gift-vouchers", "Gift Vouchers"], ["/guarantee", "Our Guarantee"], ["/emergency", "Emergency services"]]],
    ["Professionals", [["/register?role=provider", "Join MyHomelyHub"], ["/provider/find-jobs", "Find Jobs"], ["/search-jobs", "Search Jobs"], ["/pricing", "Pricing"], ["/login", "Professional Login"]]],
    ["MyHomelyHub", [["/about", "About Us"], ["/adverts", "Service Adverts"], ["/contact", "Contact"], ["/faq", "FAQs"], ["/advice", "Blog"], ["/trust-safety", "Trust & Safety"]]],
    ["Legal", [["/legal/terms", "Terms & Conditions"], ["/legal/privacy-policy", "Privacy Policy"], ["/legal/cookies-policy", "Cookies Policy"], ["/legal/refund-policy", "Refund & Dispute Policy"], ["/legal/service-provider-terms", "Service Provider Terms"], ["/legal/personal-data-policy", "Personal Data Policy"], ["/legal/acceptable-use", "Acceptable Use"], ["/legal/complaints-policy", "Complaints"]]],
  ];
  return (
    <footer className="bg-[#2B3B48] text-slate-300 mt-24" data-testid="site-footer">
      <div className="container-x py-16 grid gap-10 lg:grid-cols-6">
        <div className="lg:col-span-2">
          <div className="bg-white inline-block rounded-xl px-3 py-2"><BrandLogo className="h-[60px]" /></div>
          <p className="mt-4 text-sm max-w-xs">{b.tagline}</p>
          <address className="not-italic text-sm mt-4 space-y-1">
            <div>{b.address}</div>
            <div>Phone: {b.phone}</div>
            <div>Email: <a className="text-white hover:text-[#FF8166]" href={`mailto:${b.email}`}>{b.email}</a></div>
          </address>
        </div>
        {cols.map(([h, links]) => (
          <div key={h}>
            <h3 className="text-white font-semibold text-sm mb-4">{h}</h3>
            <ul className="space-y-2 text-sm">{links.map(([to, l]) => <li key={to}><Link to={to} className="hover:text-[#FF8166] transition-colors">{l}</Link></li>)}</ul>
          </div>
        ))}
      </div>
      <div className="border-t border-white/10"><div className="container-x py-6 text-xs text-slate-400">© {new Date().getFullYear()} {b.company}. {b.registration}</div></div>
    </footer>
  );
}

function CookieBanner() {
  const [show, setShow] = useState(!localStorage.getItem("mhh_cookies"));
  if (!show) return null;
  const choose = (v) => {
    let anon = localStorage.getItem("mhh_anon") || crypto.randomUUID();
    localStorage.setItem("mhh_anon", anon);
    localStorage.setItem("mhh_cookies", v);
    api.post("/consents", { type: "cookies", value: v, anon_id: anon }).catch(() => {});
    setShow(false);
  };
  return (
    <div className="fixed bottom-[68px] sm:bottom-0 inset-x-0 bg-white border-t border-slate-200 shadow-[0_-4px_20px_rgba(15,23,42,0.08)] z-50 fade-up" role="dialog" aria-label="Cookie consent" data-testid="cookie-banner">
      <div className="container-x py-3 flex flex-col sm:flex-row gap-3 sm:items-center">
      <p className="text-xs sm:text-sm text-slate-600 flex-1">We use essential cookies to run MyHomelyHub and, with your consent, analytics cookies to improve it. See our <Link to="/legal/cookies-policy" className="underline">Cookies Policy</Link>.</p>
      <div className="flex gap-2 shrink-0">
        <button className="btn-navy btn-sm" onClick={() => choose("all")} data-testid="cookie-accept-btn">Accept all</button>
        <button className="btn-ghost btn-sm" onClick={() => choose("essential")} data-testid="cookie-essential-btn">Essential only</button>
      </div>
      </div>
    </div>
  );
}

export function PublicLayout() {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:p-3 focus:bg-white z-50">Skip to content</a>
      <Header />
      <main id="main" className="flex-1"><Outlet /></main>
      <Footer />
      <div className="sm:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200 p-3 grid grid-cols-2 gap-2" data-testid="mobile-sticky-cta">
        <Link to="/search" className="btn-ghost btn-sm"><Search size={14} />Find a Pro</Link>
        <Link to="/post-job" className="btn-coral btn-sm"><PlusCircle size={14} />Post a Job</Link>
      </div>
      <CookieBanner />
    </div>
  );
}

export function Protected({ roles, children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <Loader />;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={dashFor(user)} replace />;
  return children;
}

export function DashLayout({ items, title, extra }) {
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <div className="max-w-[1440px] mx-auto lg:flex">
        <aside className="lg:w-60 lg:shrink-0 lg:sticky lg:top-24 lg:h-[calc(100vh-6rem)] lg:border-r border-slate-200 bg-white lg:bg-transparent" aria-label={`${title} navigation`}>
          <div className="hidden lg:block px-5 pt-6 pb-2 eyebrow">{title}</div>
          <nav className="flex lg:flex-col gap-1 overflow-x-auto p-3 lg:p-3 border-b lg:border-0 border-slate-200">
            {items.map(([to, label, Icon, end]) => (
              <NavLink key={to} to={to} end={end} data-testid={`side-nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) => `flex items-center gap-2.5 whitespace-nowrap rounded-xl px-3 py-2 text-sm font-medium transition-colors ${isActive ? "bg-[#374B5C] text-white" : "text-slate-600 hover:bg-white hover:text-[#374B5C]"}`}>
                <Icon size={16} />{label}
              </NavLink>
            ))}
          </nav>
        </aside>
        <main id="main" className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8"><AlertsBanner />{extra}<Outlet /></main>
      </div>
    </div>
  );
}
